import { useState, useEffect, useRef } from"react";
import {
 Send,
 Phone,
 Mail,
 Facebook,
 Instagram
} from"lucide-react";
import Globals from '../Globals';
import { useToast } from '../components/Toast';

const API=Globals.API;
const Billets = () => {
 const showToast = useToast();
 const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
 const heroRef = useRef(null);
 const heroContentRef = useRef(null);
 const contactPanelRef = useRef(null);

 const [scrollY, setScrollY] = useState(0);
 const [contactVisible, setContactVisible] = useState(false);

 // ---------- SCROLL EFFECTS ----------
 useEffect(() => {
 const handleScroll = () => setScrollY(window.scrollY);
 window.addEventListener("scroll", handleScroll);
 return () => window.removeEventListener("scroll", handleScroll);
 }, []);

 useEffect(() => {
 const observer = new IntersectionObserver(
 ([entry]) => {
 if (entry.isIntersecting) setContactVisible(true);
 },
 { threshold: 0.2 }
 );
 if (contactPanelRef.current) observer.observe(contactPanelRef.current);
 return () => observer.disconnect();
 }, []);

 useEffect(() => {
 const handleResize = () => setIsMobile(window.innerWidth < 640);
 window.addEventListener("resize", handleResize);
 return () => window.removeEventListener("resize", handleResize);
 }, []);

 // ---------- FORM STATE ----------
 const [formData, setFormData] = useState({
 firstName:"",
 lastName:"",
 phone:"",
 email:"",
 destination:"",
 message:"",
 dateDepart:"",
 dateRetour:"",
 classe:"",
 });

 const [submitting, setSubmitting] = useState(false);
 const [showPassengerModal, setShowPassengerModal] = useState(false);
 const [passengers, setPassengers] = useState({
 adultes: 1,
 enfants: 0,
 bebes: 0,
 });

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSubmitting(true);

 // Prepare data for API
 const requestData = {
 firstName: formData.firstName,
 lastName: formData.lastName,
 phone: formData.phone,
 email: formData.email,
 destination: formData.destination,
 dateDepart: formData.dateDepart,
 dateRetour: formData.dateRetour,
 adultes: passengers.adultes.toString(),
 enfants: passengers.enfants.toString(),
 bebes: passengers.bebes.toString(),
 classe: formData.classe,
 message: formData.message,
 };

 try {
 const response = await fetch(`${API}/reservations/billets`, {
 method:"POST",
 headers: {
"Content-Type":"application/json",
 },
 body: JSON.stringify(requestData),
 });

 const data = await response.json();

 if (response.ok) {
 showToast("Votre demande de billets a été envoyée avec succès !", "success");
 // Reset form
 setFormData({
 firstName:"",
 lastName:"",
 phone:"",
 email:"",
 destination:"",
 message:"",
 dateDepart:"",
 dateRetour:"",
 classe:"",
 });
 setPassengers({
 adultes: 1,
 enfants: 0,
 bebes: 0,
 });
 } else {
 showToast(`Erreur: ${data.message ||"Échec de l'envoi"}`, "error");
 }
 } catch (error) {
 console.error("Error:", error);
 showToast("Erreur de connexion au serveur. Veuillez réessayer.", "error");
 } finally {
 setSubmitting(false);
 }
 };

 const handleChange = (e) =>
 setFormData({ ...formData, [e.target.name]: e.target.value });

 const updatePassengers = (type, action) => {
 setPassengers((prev) => {
 const newValue =
 action ==="increment" ? prev[type] + 1 : Math.max(0, prev[type] - 1);
 if (type ==="adultes" && newValue < 1) return prev;
 return { ...prev, [type]: newValue };
 });
 };

 const getTotalPassengers = () => {
 return passengers.adultes + passengers.enfants + passengers.bebes;
 };

 const getPassengerText = () => {
 const total = getTotalPassengers();
 if (total === 1) return"1 Passager (1 adulte)";
 const parts = [];
 if (passengers.adultes > 0)
 parts.push(`${passengers.adultes} adulte${passengers.adultes > 1 ?"s" :""}`);
 if (passengers.enfants > 0)
 parts.push(`${passengers.enfants} enfant${passengers.enfants > 1 ?"s" :""}`);
 if (passengers.bebes > 0)
 parts.push(`${passengers.bebes} bébé${passengers.bebes > 1 ?"s" :""}`);
 return `${total} Passager${total > 1 ?"s" :""} (${parts.join(",")})`;
 };

 // ---------- STYLES ----------
 const containerStyle = {
 maxWidth:"1200px",
 margin: isMobile ?"20px auto 0" :"-180px auto 0",
 padding: isMobile ?"0 15px 60px" :"0 20px 80px",
 position:"relative",
 zIndex: 10,
 };

 const cardStyle = {
 display:"flex",
 flexDirection: isMobile ?"column" :"row",
 background:"white",
 borderRadius:"16px",
 overflow:"hidden",
 boxShadow:"0 25px 70px rgba(0,0,0,0.15)",
 };

 const formSectionStyle = {
 flex: 1.3,
 padding: isMobile ?"40px 25px" :"60px 50px",
 background:"white",
 };

 const contactSectionStyle = {
 flex: 0.7,
 padding: isMobile ?"40px 25px" :"60px 40px",
 background:"#01225C",
 color:"white",
 display:"flex",
 flexDirection:"column",
 justifyContent:"space-between",
 transform: contactVisible ?"translateX(0)" :"translateX(100px)",
 opacity: contactVisible ? 1 : 0,
 transition:"all 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
 };

 const twoColStyle = {
 display:"grid",
 gridTemplateColumns: isMobile ?"1fr" :"1fr 1fr",
 gap:"20px",
 };

 const inputBase = {
 padding:"16px 18px",
 border:"2px solid #CBDEFC",
 borderRadius:"8px",
 fontSize:"15px",
 outline:"none",
 transition:"all 0.3s",
 background:"#FBFDFF",
 color:"#334155",
 width:"100%",
 };

 const selectStyle = {
 ...inputBase,
 cursor:"pointer",
 };

 return (
 <div
 style={{
 minHeight:"100vh",
 background:"#f1f5f9",
 fontFamily:"system-ui, -apple-system, sans-serif",
 }}
 >
 {/* HERO */}
 <div
 ref={heroRef}
 style={{
 minHeight:"500px",
 background: `linear-gradient(135deg, rgba(67, 103, 150, 0.26) 0%, rgba(90, 116, 171, 0.55) 20%), url("https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600") center/cover`,
 display:"flex",
 flexDirection:"column",
 alignItems:"center",
 justifyContent:"center",
 color:"white",
 padding:"0 20px",
 transform: `translateY(${scrollY * 0.3}px)`,
 }}
 >
 <h1
 ref={heroContentRef}
 style={{
 fontSize:"46px",
 fontWeight:"700",
 marginBottom:"20px",
 textAlign:"center",
 opacity: Math.max(0, 1 - scrollY / 300),
 transition:"opacity 0.2s, transform 0.2s",
 transform: `translateY(${scrollY * 0.2}px)`,
 color: '#ffff'
 }}
 >
 Prenez Contact
 </h1>
 <p
 style={{
 fontSize:"18px",
 opacity: Math.max(0, 0.95 - scrollY / 400),
 textAlign:"center",
 transition:"opacity 0.2s, transform 0.2s",
 transform: `translateY(${scrollY * 0.15}px)`,
 color: '#ffff'
 }}
 >
 N'hésitez pas — nous sommes prêts à organiser votre voyage.
 </p>
 </div>

 {/* FORM CARD */}
 <div style={containerStyle}>
 <div style={cardStyle}>
 {/* LEFT: FORM */}
 <div style={formSectionStyle}>
 <div
 style={{
 display:"flex",
 alignItems:"center",
 gap:"12px",
 marginBottom:"40px",
 }}
 >
 <Send size={28} color="#FFBC1E" />
 <h2
 style={{
 fontSize:"28px",
 fontWeight:"700",
 color:"#00338C",
 margin: 0,
 }}
 >
 Réserver Maintenant
 </h2>
 </div>

 <div style={{ display:"flex", flexDirection:"column", gap:"24px" }}>
 {/* Name */}
 <div style={twoColStyle}>
 {["firstName","lastName"].map((field, i) => (
 <input
 key={field}
 placeholder={field ==="firstName" ?"Nom" :"Prénom"}
 name={field}
 value={formData[field]}
 onChange={handleChange}
 style={{
 ...inputBase,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition: `all 0.5s ease ${(i + 1) * 0.1}s`,
 }}
 />
 ))}
 </div>

 {/* Phone + Email */}
 <div style={twoColStyle}>
 {["phone","email"].map((field, i) => (
 <input
 key={field}
 placeholder={field ==="phone" ?"Téléphone" :"Email"}
 name={field}
 value={formData[field]}
 onChange={handleChange}
 style={{
 ...inputBase,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition: `all 0.5s ease ${(i + 3) * 0.1}s`,
 }}
 />
 ))}
 </div>

 {/* Destination */}
 <input
 placeholder="Destination"
 name="destination"
 value={formData.destination}
 onChange={handleChange}
 style={{
 ...inputBase,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition: `all 0.5s ease 0.5s`,
 }}
 />

 {/* Dates */}
 <div style={twoColStyle}>
 <input
 type="date"
 name="dateDepart"
 value={formData.dateDepart}
 onChange={handleChange}
 style={{
 ...inputBase,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition:"all 0.5s ease 0.6s",
 }}
 />
 <input
 type="date"
 name="dateRetour"
 value={formData.dateRetour}
 onChange={handleChange}
 style={{
 ...inputBase,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition:"all 0.5s ease 0.7s",
 }}
 />
 </div>

 {/* Passagers Input */}
 <div style={{ position:"relative" }}>
 <div
 onClick={() => setShowPassengerModal(!showPassengerModal)}
 style={{
 ...inputBase,
 cursor:"pointer",
 display:"flex",
 justifyContent:"space-between",
 alignItems:"center",
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition:"all 0.5s ease 0.75s",
 }}
 >
 <span>{getPassengerText()}</span>
 <span style={{ fontSize:"20px" }}>
 {showPassengerModal ?"▲" :"▼"}
 </span>
 </div>

 {showPassengerModal && (
 <div
 style={{
 position:"absolute",
 top:"calc(100% + 8px)",
 left: 0,
 right: 0,
 background:"white",
 borderRadius:"12px",
 boxShadow:"0 10px 40px rgba(0,0,0,0.15)",
 padding:"24px",
 zIndex: 100,
 animation:"slideDown 0.3s ease",
 }}
 >
 <div
 style={{
 display:"flex",
 justifyContent:"space-between",
 alignItems:"center",
 marginBottom:"10px",
 paddingBottom:"10px",
 borderBottom:"1px solid #e2e8f0",
 }}
 >
 <h3 style={{ fontSize:"18px", fontWeight:"700", color:"#1e293b", margin: 0 }}>
 Passagers
 </h3>
 <button
 type="button"
 onClick={() => setShowPassengerModal(false)}
 style={{
 background:"none",
 border:"none",
 fontSize:"24px",
 cursor:"pointer",
 color:"#64748b",
 }}
 >
 ×
 </button>
 </div>

 {[
 { key:"adultes", label:"Adulte(s)", subtitle:"12 ans et plus" },
 { key:"enfants", label:"Enfant(s)", subtitle:"2-11 ans" },
 { key:"bebes", label:"Bébé(s)", subtitle:"Moins de 2 ans" },
 ].map((item) => (
 <div
 key={item.key}
 style={{
 display:"flex",
 justifyContent:"space-between",
 alignItems:"center",
 padding:"16px 0",
 borderBottom:"1px solid #f1f5f9",
 }}
 >
 <div>
 <div style={{ fontSize:"16px", fontWeight:"600", color:"#1e293b" }}>
 {item.label}
 </div>
 <div style={{ fontSize:"13px", color:"#64748b" }}>
 {item.subtitle}
 </div>
 </div>
 <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
 <button
 type="button"
 onClick={() => updatePassengers(item.key,"decrement")}
 disabled={
 item.key ==="adultes" && passengers[item.key] <= 1
 }
 style={{
 width:"36px",
 height:"36px",
 borderRadius:"50%",
 border:"2px solid #cbd5e1",
 background:"white",
 cursor:"pointer",
 fontSize:"18px",
 fontWeight:"700",
 color:"#475569",
 opacity:
 item.key ==="adultes" && passengers[item.key] <= 1
 ? 0.3
 : 1,
 }}
 >
 −
 </button>
 <span
 style={{
 fontSize:"18px",
 fontWeight:"600",
 minWidth:"24px",
 textAlign:"center",
 }}
 >
 {passengers[item.key]}
 </span>
 <button
 type="button"
 onClick={() => updatePassengers(item.key,"increment")}
 style={{
 width:"36px",
 height:"36px",
 borderRadius:"50%",
 border:"2px solid #2563eb",
 background:"#2563eb",
 cursor:"pointer",
 fontSize:"18px",
 fontWeight:"700",
 color:"white",
 }}
 >
 +
 </button>
 </div>
 </div>
 ))}

 <button
 type="button"
 onClick={() => setShowPassengerModal(false)}
 style={{
 marginTop:"20px",
 padding:"12px 24px",
 background:"#2563eb",
 color:"white",
 border:"none",
 borderRadius:"8px",
 fontSize:"15px",
 fontWeight:"600",
 cursor:"pointer",
 width:"100%",
 }}
 >
 Valider et Fermer
 </button>
 </div>
 )}
 </div>

 <select
 name="classe"
 value={formData.classe}
 onChange={handleChange}
 style={{
 ...selectStyle,
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition:"all 0.5s ease 0.8s",
 }}
 >
 <option value="">Économique-Sans bagage</option>
 <option value="economique">Économique-Avec bagage</option>
 <option value="business">Business-Sans bagage</option>
 <option value="business">Business-Avec bagage</option>
 </select>

 {/* Message */}
 <textarea
 placeholder="Remarque ou message ?"
 name="message"
 rows="5"
 value={formData.message}
 onChange={handleChange}
 style={{
 ...inputBase,
 resize:"vertical",
 opacity: contactVisible ? 1 : 0,
 transform: contactVisible ?"translateY(0)" :"translateY(30px)",
 transition:"all 0.5s ease 0.9s",
 }}
 />

 {/* Submit */}
 <div style={{ display:"flex", justifyContent:"center" }}>
 <button
 type="button"
 onClick={handleSubmit}
 disabled={submitting}
 style={{
 padding:"16px 48px",
 background:"#00338C",
 color:"white",
 border:"none",
 borderRadius:"8px",
 fontSize:"16px",
 fontWeight:"700",
 cursor: submitting ?"not-allowed" :"pointer",
 display:"flex",
 alignItems:"center",
 gap:"12px",
 transition:"all 0.2s",
 opacity: submitting ? 0.7 : 1,
 }}
 >
 <Send size={18} />
 {submitting ?"ENVOI..." :"ENVOYER"}
 </button>
 </div>
 </div>
 </div>

 {/* RIGHT: CONTACT INFO */}
 <div ref={contactPanelRef} style={contactSectionStyle}>
 <div>
 <h2
 style={{
 fontSize:"26px",
 fontWeight:"700",
 marginBottom:"40px",
 color:"#ffff",
 }}
 >
 Infos de Contact
 </h2>

 <div style={{ display:"flex", alignItems:"center", gap:"18px", marginBottom:"32px" }}>
 <div
 style={{
 width:"52px",
 height:"52px",
 background:"#FFBC1E",
 borderRadius:"50%",
 display:"flex",
 alignItems:"center",
 justifyContent:"center",
 }}
 >
 <Phone size={22} />
 </div>
 <span style={{ fontSize:"17px", fontWeight:"600" }}>+216 26 108 000</span>
 </div>

 <div style={{ display:"flex", alignItems:"center", gap:"18px", marginBottom:"32px" }}>
 <div
 style={{
 width:"52px",
 height:"52px",
 background:"#FFBC1E",
 borderRadius:"50%",
 display:"flex",
 alignItems:"center",
 justifyContent:"center",
 }}
 >
 <Mail size={22} />
 </div>
 <span style={{ fontSize:"17px", fontWeight:"600" }}>myriam@aviatortravel.tn</span>
 </div>
 </div>

 <div style={{ marginTop:"40px" }}>
 <div style={{ display:"flex", gap:"14px" }}>
 {[
 { Icon: Facebook, link:"https://www.facebook.com/AVIATORTN" }, 
 { Icon: Instagram, link:"https://www.instagram.com/aviator_travel_services/?hl=fr" }
 ].map(({ Icon, link }, i) => (
 <a
 key={i}
 href={link}
 target="_blank"
 rel="noopener noreferrer"
 style={{
 width:"52px",
 height:"52px",
 background:"rgba(255,255,255,0.2)",
 border:"none",
 borderRadius:"50%",
 display:"flex",
 alignItems:"center",
 justifyContent:"center",
 cursor:"pointer",
 transition:"all 0.3s",
 color:"white",
 textDecoration:"none"
 }}
 >
 <Icon size={22} />
 </a>
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* FOOTER */}
 <div
 style={{
 padding:"60px 20px",
 background:"#f1f5f9",
 textAlign:"center",
 }}
 >
 <h3
 style={{
 fontSize:"32px",
 fontWeight:"700",
 color:"#1e293b",
 marginBottom:"20px",
 }}
 >
 Nous Sommes Là Pour Vous
 </h3>
 <p
 style={{
 fontSize:"18px",
 color:"#64748b",
 maxWidth:"600px",
 margin:"0 auto",
 lineHeight:"1.6",
 }}
 >
 Notre équipe est disponible pour répondre à toutes vos questions et
 vous accompagner dans vos projets de voyage.
 </p>
 </div>

 {/* Animation Keyframes */}
 <style>
 {`
 @keyframes slideDown {
 from {
 opacity: 0;
 transform: translateY(-10px);
 }
 to {
 opacity: 1;
 transform: translateY(0);
 }
 }
 `}
 </style>
 </div>
 );
};

export default Billets;